import queryString from 'query-string';

import { ROUTE_PATHS } from '../../config/routes';
import { ALLOWED_JOIN_MEMBERSHIP_PARAMS } from '../../utils/constants';

import healthMetrics from '../../utils/health-metrics/health-metrics';
import Logger from '../../utils/logger/logger';

import analytics from '../../../shared/analytics';
import ClickTracker from '../../../shared/click-tracker/click-tracker';
import UiElement from '../../../shared/ui-element/ui-element';

import StoreEmailSubscription from '../store-email-subscription-base/store-email-subscription-base';
import StoreLangSwitcher from '../store-lang-switcher/store-lang-switcher';
import StoreNavigation from '../store-navigation/store-navigation';
import StoreSidebar from '../store-sidebar/store-sidebar';
import StoreSearch from '../store-search/store-search';
import Footer from '../footer/footer';
import SignupModal from '../signup-modal/signup-modal';
import RedirectTo from '../redirect-to/redirect-to';
import CaptchaProvider from '../../../shared/captcha-provider/captcha-provider';
import BlogSubscribeBanner from '../blog-subscribe-banner/blog-subscribe-banner';

import '../../main.scss';

class BasePage extends UiElement {
  signupModalOptions = {
    hideCloseBtn: false,
  };

  constructor() {
    super();

    this.ui = {
      subscribeBanner: 'js-subscribe-banner',
    };

    this.captchaProvider = new CaptchaProvider();
    this.signupModal = new SignupModal(this.captchaProvider);
    this.analytics = analytics;
  }

  onInit() {
    Logger.initialize();

    this.captchaProvider.init();

    new StoreNavigation({ rootElement: '.js-store-navigation' }).init();
    new StoreSidebar({ rootElement: '.js-store-sidebar' }).init();
    new StoreSearch({ rootElement: '.js-store-search' }).init();
    new StoreSearch({ rootElement: '.js-store-sidebar-search' }).init();

    const storeSubscriptionForm = document.querySelector('.js-store-subscription-form');
    if (storeSubscriptionForm) {
      new StoreEmailSubscription({ rootElement: storeSubscriptionForm }).init();
    }

    const storeLangSwitcher = document.querySelector('.js-store-lang-switcher');
    if (storeLangSwitcher) {
      new StoreLangSwitcher({ rootElement: storeLangSwitcher }).init();
    }

    if (this.ui.subscribeBanner) {
      new BlogSubscribeBanner({ rootElement: '.js-subscribe-banner' }, this.captchaProvider).init();
    }

    new ClickTracker().init();
    new RedirectTo().init();
    new Footer().init();
    healthMetrics.initialize();

    this.signupModal.init();
    this.setSignupModalOptions();
    this.initSignUpButtons();
    this.openSignupModalFromExternalSourceIfNeeded();
    this.trackPageView();
  }

  getJoinMembershipUrl({ tierType, signupHideSocials, eventLabel, campaign } = {}) {
    const { joinMembership } = ROUTE_PATHS.newWebsitePages;

    const sourceParams = new URLSearchParams(window.location.search);
    const filteredParams = new URLSearchParams();

    sourceParams.forEach((value, key) => {
      if (ALLOWED_JOIN_MEMBERSHIP_PARAMS.has(key)) {
        filteredParams.set(key, value);
      }
    });

    if (tierType) filteredParams.set('tierType', tierType);
    if (signupHideSocials) filteredParams.set('signupHideSocials', signupHideSocials);
    if (eventLabel) filteredParams.set('eventLabel', eventLabel);
    if (campaign && !filteredParams.has('campaign')) filteredParams.set('campaign', campaign);

    const searchString = filteredParams.toString() ? `?${filteredParams.toString()}` : '';

    return `${joinMembership}${searchString}`;
  }

  setSignupModalOptions() {
    const query = queryString.parse(window.location.search);

    if ('hideModalCloseBtn' in query) {
      this.signupModalOptions = { ...this.signupModalOptions, hideCloseBtn: true };
    }
  }

  trackPageView() {
    healthMetrics.trackIncrement({ name: 'page_view' });
  }

  initSignUpButtons() {
    const websiteCommonSignupButtons = document.querySelectorAll('[data-signup-button]');
    const blogSliderSignupButtons = document.querySelectorAll('.js-slider-banner-cta');
    const blogPromoBannerSignupButtons = document.querySelectorAll('.js-promo-banner-cta');
    const blogSideScrollingSignupButtons = document.querySelectorAll('.js-side-scrolling-cta');

    // Side-scrolling CTA matches both [data-signup-button] and .js-side-scrolling-cta,
    // so dedupe to avoid processing the same node twice.
    const buttons = new Set([
      ...websiteCommonSignupButtons,
      ...blogSliderSignupButtons,
      ...blogPromoBannerSignupButtons,
      ...blogSideScrollingSignupButtons,
    ]);

    buttons.forEach((button) => {
      this.signupButtonToLink(button);
    });
  }

  isSignInOrSignUpUrl(href) {
    try {
      const { pathname } = new URL(href, window.location.origin);
      const lastSegment = pathname.replace(/\/+$/, '').split('/').pop();

      return lastSegment === 'signin' || lastSegment === 'signup';
    } catch {
      return false;
    }
  }

  isSafeNavigationUrl(href) {
    try {
      const { protocol } = new URL(href, window.location.origin);

      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  }

  signupButtonToLink(button) {
    const href = button.getAttribute('href');

    if (href && !this.isSignInOrSignUpUrl(href) && this.isSafeNavigationUrl(href)) {
      return;
    }

    const { tierType, signupHideSocials, eventLabel, campaign, ...restDataAttributes } = button.dataset;
    const joinUrl = this.getJoinMembershipUrl({ tierType, signupHideSocials, eventLabel, campaign });

    if (button.tagName === 'A') {
      button.setAttribute('href', joinUrl);
      button.removeAttribute('target');
      button.removeAttribute('rel');

      return;
    }

    const link = document.createElement('a');

    link.href = joinUrl;
    link.innerHTML = button.innerHTML;
    link.className = button.className;

    if (button.id) {
      link.id = button.id;
    }

    Object.entries(restDataAttributes).forEach(([key, value]) => {
      link.dataset[key] = value;
    });

    button.replaceWith(link);
  }

  // This is a function to open Signup modal for
  // those users who came from external landings with additional get params
  openSignupModalFromExternalSourceIfNeeded() {
    const query = queryString.parse(window.location.search);
    const externalParamsInUrl = ['appId', 'affiliates'];

    externalParamsInUrl.some((param) => {
      if (param in query) {
        this.signupModal.open(this.signupModalOptions);

        return true;
      }

      return false;
    });
  }
}

export default BasePage;
